<template>
  <div>
    <div class="text-center text-vibrant-purple text-weight-bold" style="font-size: 2.2rem">
      Real-time data & analytics for your portfolio
    </div>
    <div class="text-center text-grey-9 q-mb-xl text-subtitle1" style="margin: 0 auto">
      Leverage daily data and insights from more than 5 million multifamily properties and gain an
      edge on your competition
    </div>
    <div class="q-mt-xl">
      <div class="row justify-center q-gutter-md">
        <q-spinner v-if="trialConsumed === null" class="text-h4 text-center full-width" />
        <template v-else>
          <div class="q-pa-md">
            <dashboard-card
              icon="code"
              title="API"
              color="primary"
              subtitle="Best to power your own software"
              style="max-width: 380px; width: 100%"
            >
              <div class="q-mb-md" style="height: 375px">
                <div class="flex items-end">
                  <div class="text-bold" style="font-size: 2.4rem; line-height: 1">$0.50</div>
                  <div
                    class="q-ml-xs q-mb-xs"
                    :class="[$q.dark.isActive ? 'text-grey-4' : 'text-grey-8']"
                    style="line-height: 1"
                  >
                    /request
                  </div>
                </div>
                <div
                  class="q-mt-md"
                  :class="[$q.dark.isActive ? 'text-grey-3' : 'text-grey-9']"
                  style="height: 80px"
                >
                  Empower your platform with daily property data and advanced analytics—built for
                  developers, designed for scale.
                </div>
                <div class="q-gutter-xs">
                  <div v-for="(f, index) in apiFeatures" :key="index">
                    <span class="cursor-pointer">
                      <q-icon name="task_alt" class="q-mr-sm" color="green-5" size="20px" />
                      <span>{{ f.label }}</span>
                      <q-icon name="info_outline" class="q-ml-xs" />
                    </span>
                    <q-tooltip
                      :ref="f.label"
                      style="width: 250px; font-size: 0.8rem"
                      transition-duration="0"
                    >
                      {{ f.extra }}
                    </q-tooltip>
                  </div>
                </div>
                <div class="text-bold q-mb-md q-mt-lg">Check out our API documentation:</div>
                <div class="q-gutter-y-sm q-mt-md">
                  <a
                    href="https://docs.hellodata.ai/"
                    target="_blank"
                    style="text-decoration: none"
                  >
                    <div
                      class="rounded-borders q-pa-sm text-primary text-weight-bold q-mb-sm q-mx-xl text-center bg-deep-purple-light"
                      style="font-size: 0.8rem"
                    >
                      API Documentation
                    </div>
                  </a>
                </div>
              </div>
              <template #footer>
                <q-btn
                  id="intercom-action"
                  href="https://www.hellodata.ai/multifamily-market-analysis-demo"
                  color="primary"
                  class="full-width q-mt-xs"
                  no-caps
                  outline
                  unelevated
                  target="_blank"
                >
                  Schedule Demo
                </q-btn>
              </template>
            </dashboard-card>
          </div>
          <dashboard-card
            icon="bolt"
            title="Professional"
            color="accent"
            subtitle="Best for individuals or small teams"
            is-primary
            shadow
            style="max-width: 380px; width: 100%"
          >
            <div class="q-mb-md" style="height: 415px">
              <div class="flex items-end">
                <div class="text-bold" style="font-size: 2.5rem; line-height: 1">$300</div>
                <div
                  class="q-ml-xs q-mb-xs"
                  :class="[$q.dark.isActive ? 'text-grey-4' : 'text-grey-8']"
                  style="line-height: 1"
                >
                  /month
                </div>
              </div>
              <div
                class="q-mt-md"
                :class="[$q.dark.isActive ? 'text-grey-3' : 'text-grey-9']"
                style="height: 40px"
              >
                Power your rent surveys with real-time data on over 35M multifamily units
                nationwide.
              </div>
              <div class="q-my-lg q-gutter-xs">
                <div v-for="(f, index) in features" :key="index">
                  <span class="cursor-pointer">
                    <q-icon name="task_alt" class="q-mr-sm" color="green-5" size="20px" />
                    <span style="font-weight: 450">{{ f.label }}</span>
                    <q-icon name="info_outline" class="q-ml-xs" />
                  </span>
                  <q-tooltip :ref="String(f.label)" style="width: 250px; font-size: 0.8rem">
                    {{ f.extra }}
                  </q-tooltip>
                </div>
              </div>
              <div class="text-bold q-mb-md">Check out our sample reports:</div>
              <div class="q-gutter-y-sm q-mt-md">
                <router-link
                  v-for="sample in propertyReportSamples"
                  :key="sample.name"
                  :to="{ name: sample.name }"
                  style="text-decoration: none"
                >
                  <div
                    class="rounded-borders q-pa-sm text-primary text-weight-bold q-mb-sm q-mx-xl text-center bg-deep-purple-light"
                    style="font-size: 0.8rem"
                  >
                    {{ sample.name }}
                  </div>
                </router-link>
              </div>
            </div>
            <template #footer>
              <q-btn
                id="intercom-action"
                :to="{ name: 'Subscribe to Property Reports' }"
                color="accent"
                class="full-width"
                no-caps
                unelevated
              >
                <template v-if="trialConsumed">Resume Your Subscription</template>
                <template v-else>Start Your 7-Day Free Trial</template>
              </q-btn>
            </template>
          </dashboard-card>
          <div class="q-pa-md">
            <dashboard-card
              icon="business"
              title="Enterprise"
              color="primary"
              subtitle="Best for national rollouts"
              style="max-width: 380px; width: 100%"
            >
              <div class="q-mb-md" style="height: 380px">
                <div class="text-bold" style="font-size: 2.4rem; line-height: 1">Per Unit</div>
                <div
                  class="q-mt-md"
                  :class="[$q.dark.isActive ? 'text-grey-3' : 'text-grey-9']"
                  style="height: 80px"
                >
                  Benefit from bulk discounts for large teams. Easily integrate your SSO and get
                  access to all features.
                </div>
                <div class="q-gutter-xs">
                  <div v-for="(f, index) in enterpriseFeatures" :key="index">
                    <span class="cursor-pointer">
                      <q-icon name="task_alt" class="q-mr-sm" color="green-5" size="20px" />
                      <span>{{ f.label }}</span>
                      <q-icon name="info_outline" class="q-ml-xs" />
                    </span>
                    <q-tooltip :ref="f.label" style="width: 250px; font-size: 0.8rem">
                      {{ f.extra }}
                    </q-tooltip>
                  </div>
                </div>
              </div>
              <template #footer>
                <q-btn
                  id="intercom-action"
                  href="https://www.hellodata.ai/multifamily-market-analysis-demo"
                  color="primary"
                  class="full-width"
                  no-caps
                  outline
                  unelevated
                  target="_blank"
                >
                  Schedule Demo
                </q-btn>
              </template>
            </dashboard-card>
          </div>
        </template>
      </div>
      <div class="q-mt-xl">
        <div class="text-center text-primary text-weight-medium">
          Our Data is Your Competitive Advantage.
        </div>
        <div class="text-center q-mb-xl text-bold" style="font-size: 1.3rem">
          The New Gold Standard in Multifamily Data
        </div>
        <div class="row q-col-gutter-md">
          <div v-for="(q, index) in quotes" :key="index" class="col-md-4 col-12 row">
            <quote-card :quote="q.text" :name="q.name" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import DashboardCard from '@/components/DashboardCard.vue'
import QuoteCard from '@/components/QuoteCard.vue'
import { propertyReportSamples } from 'common'
import { useStripeStore } from '@/stores/stripe'

export default {
  name: 'PropertyReportsNotSubscribed',
  components: { DashboardCard, QuoteCard },
  data() {
    return {
      propertyReportSamples,
      apiFeatures: [
        {
          label: 'Rent Comps API',
          extra: 'Find statistically relevant comps using computer vision and AI.'
        },
        {
          label: 'Property Data API',
          extra:
            'Track rent, availability, concessions, and time on market—updated daily for over 5M multifamily properties.'
        },
        {
          label: 'Revenue Management API',
          extra: 'Optimize pricing across portfolios using only public data.'
        },
        {
          label: 'Financial Analysis API',
          extra: 'Benchmark operating expenses with real data from 25,000+ multifamily properties.'
        },
        {
          label: 'High-Volume Discounts',
          extra:
            'Get lower rates by committing to a minimum volume of API calls, starting at 10,000 per month.'
        }
      ],
      features: [
        {
          label: '100% Public Data',
          extra: 'All data points are collected from public sources.'
        },
        {
          label: '10 Customized Reports',
          extra: 'Analyze up to 10 properties.'
        },
        {
          label: 'Automatic Rent Comp Analysis',
          extra:
            'Instantly analyze nearby comps by year built, unit count, and quality to compare pricing and rent trends.'
        },
        {
          label: 'Rent Pricing Recommendations',
          extra: 'Get unit-level pricing suggestions based entirely on public data.'
        },
        {
          label: 'Financial Analysis ($200/mo add-on)',
          extra:
            'Estimate stabilized income and expenses over 12 months using benchmarks from similar properties.'
        },
        {
          label: 'Market Analytics ($200/mo add-on)',
          extra:
            'Track rent and availability trends within your custom market. Free during trial, then $200/month.'
        }
      ],
      enterpriseFeatures: [
        {
          label: 'Unlimited Rent Survey Seats',
          extra: 'No seat limits. Run as many reports as your team needs.'
        },
        {
          label: 'Bulk Data Downloads',
          extra: 'Export large datasets for custom or large-scale analysis.'
        },
        {
          label: 'SSO Integration',
          extra: 'Enable SAML-based SSO to simplify access and security.'
        },
        {
          label: 'White Labeled PDFs',
          extra: 'Add your logo and branding to reports—we’ll handle the rest.'
        },
        {
          label: 'Discounted API Usage',
          extra: 'Get lower rates on API calls for seamless integration at scale.'
        },
        {
          label: 'Fast Onboarding',
          extra:
            'Just search for your properties and add them to your portfolio—onboarding is that simple.'
        }
      ],
      quotes: [
        {
          text: 'Adding HelloData to our workflows has significantly improved the sophistication of our competitive analytics. The tool is easy to integrate with our existing spreadsheets, making for a seamless addition to our workflow.',
          name: 'Robert B.'
        },
        {
          text: "This platform provides excellent quality analytical insights into competitive intelligence and revenue management. It's easy to use and integrate with existing workflows, and the team is extremely supportive. Highly recommend!",
          name: 'Steven J.'
        },
        {
          text: "HelloData's APIs are well-documented and very easy to use. I was impressed with the data quality and the team's responsiveness during the integration.",
          name: 'Nicholas K.'
        },
        {
          text: 'This software provides data on rent concessions and rent trends for previous months at multifamily properties. This helps increase the quality of a rent comp analysis.',
          name: 'Eric J.'
        },
        {
          text: "This product makes it so easy to find relevant multifamily rent comps. If you're looking to save time copying and pasting from property websites, I'd highly recommend checking it out.",
          name: 'Roswald W.'
        },
        {
          text: "Can't beat HelloData.ai. Best data in the industry and the platform is extremely easy to navigate. Highly recommend!",
          name: 'Evan S.'
        }
      ]
    }
  },
  computed: {
    trialConsumed() {
      return useStripeStore().propertyReportTrialConsumed
    }
  }
}
</script>

<style scoped>
.text-vibrant-purple {
  color: #4f46e5;
}
</style>
